import { Checkbox, FormControl, ListItemText, MenuItem, Select, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from 'react';
import { AVAILABILITY_TYPES } from 'util/Constants';
import ClearFilterButton from './ClearFilterButton';

interface MultipleAvailabilityFilterProps {
    placeholder: string;
    value: string[];
    onChanged: (value: string[]) => void;
    showClearButton?: boolean;
    displayClassName?: string;
    'data-cy'?: string;
}

const MultipleAvailabilityFilter = ({
    value,
    onChanged,
    placeholder,
    showClearButton = true,
    displayClassName = '',
    'data-cy': dataCy,
}: MultipleAvailabilityFilterProps) => {
    const [selectedValues, setSelectedValues] = useState<string[]>([]);

    useEffect(() => {
        if (value && Array.isArray(value)) {
            // Convert incoming lowercase values to proper capitalized display values
            const displayValues = value.map((val) => {
                const lowerVal = val.toLowerCase();
                switch (lowerVal) {
                    case 'available':
                        return 'Available';
                    case 'rented':
                        return 'Rented';
                    case 'reserved':
                        return 'Reserved';
                    case 'sold':
                        return 'Sold';
                    default:
                        return val; // Keep original if not recognized
                }
            });
            setSelectedValues(displayValues);
        } else {
            setSelectedValues([]);
        }
    }, [value]);

    const handleChange = (event: any) => {
        const selectedValue = event.target.value;
        const newSelectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
        setSelectedValues(newSelectedValues);
        const lowercaseValues = newSelectedValues.map((value) => value.toLowerCase());
        onChanged(lowercaseValues);
    };

    const clearFilterClicked = () => {
        setSelectedValues([]);
        onChanged([]);
    };

    const renderValue = (selected: unknown) => {
        if (!selectedValues || selectedValues.length === 0) {
            return placeholder;
        }
        return selectedValues.join(', ');
    };

    return (
        <div className="relative w-full h-full">
            <FormControl fullWidth className="!h-full">
                <Select
                    multiple
                    value={selectedValues}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={renderValue}
                    className={`!w-full !h-full rounded-[8px] border-[#DFE1E1] leading-6 px-1 ${displayClassName}`}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {AVAILABILITY_TYPES.map((type) => (
                        <MenuItem key={type} value={type} data-cy={dataCy}>
                            <Checkbox
                                checked={selectedValues.includes(type)}
                                sx={{
                                    padding: '0 8px 0 0',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        fontSize: '20px',
                                    },
                                }}
                            />
                            <ListItemText
                                primary={type}
                                sx={{
                                    m: 0,
                                }}
                            />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Info icon for tooltip */}
            <div className="absolute right-8 top-1/2 transform -translate-y-1/2 z-10">
                <Tooltip
                    title="Our AI detects listings that are rented, reserved or sold in most cases. However, all websites are different, especially when the 'sold' or similar text is on the picture, and not text, our AI can not always detect this. We will add a button so that users can notify a property is no longer available, making our joint effort to create a clean and transparent market easier."
                    arrow
                    placement="top"
                    sx={{
                        '& .MuiTooltip-tooltip': {
                            maxWidth: '300px',
                            fontSize: '12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.87)',
                        },
                    }}
                >
                    <InfoOutlinedIcon
                        className="text-gray-400 cursor-help hover:text-gray-600"
                        style={{ fontSize: '16px' }}
                        data-cy="availability-filter-info-icon"
                    />
                </Tooltip>
            </div>

            {/* clear button */}
            {value && value.length > 0 && showClearButton && <ClearFilterButton onClicked={clearFilterClicked} />}
        </div>
    );
};

export default MultipleAvailabilityFilter;
